import { useEffect, useState } from "react"
import axios from "axios"
import { Box, Button, Flex, Heading, Link, List, Presence, Spinner, Stack, Text } from "@chakra-ui/react"
import { FaArrowLeft } from "react-icons/fa"
import getOktaConfig from "lib/getOktaConfig"
import { getPageData } from "services/craft/craft"
import runMiddleware from "lib/runMiddleware"
import Layout from "components/layout/layout"
import Wrapper from "components/layout/wrapper"
import CustomLoginForm from "components/registration/customLoginForm"
import CreateAccountSection from "components/registration/createAccountSection"
import StyledButton from "components/registration/StyledButton"

const textDefaults = {
    accountLoginHeading: "Register with your TCTMD Account",
    signUpText: "Sign up for a free TCTMD account to register.",
}

const checkForOktaSession = async issuer => {
    let oktaUser = null
    await axios
        .get(issuer + "/api/v1/sessions/me", { withCredentials: true })
        .then(response => {
            console.log("Found users session for", response.data)
            oktaUser = {
                isLoggedIn: true,
                oktaId: response.data?.userId,
                name: response.data?._links?.user?.name,
                sessionId: response.data?.id,
                // email: response.data.login,
            }
        })
        .catch(error => {
            console.log("No Okta session found", error)
        })

    return oktaUser
}

const killOktaSessionOld = async (sessionId, preview = false) => {
    return await axios
        .get(`/api/okta/session/clear/${sessionId}`, {
            headers: { preview: preview },
        })
        .then(response => {
            console.log("Okta session killed", response.status)
            return response.status
        })
        .catch(error => {
            console.log("No Okta session found", error)
        })
}

export default function RegistrationPage({ page, layoutVariant, site, locale, vendorOktaClientId }) {
    const [pageStatus, setPageStatus] = useState("loading")

    const oktaConfig = getOktaConfig(page.preview)

    useEffect(() => {
        const _killOktaSession = async () => {
            const _user = await checkForOktaSession(oktaConfig.issuer)
            if (_user) {
                // close any active Okta session for user
                await killOktaSessionOld(_user?.sessionId, page.preview)
                setPageStatus("default")

                return
            }
            setPageStatus("default")
        }
        _killOktaSession()
    }, [])
    //{site.meeting.meetingName}
    return (
        <Layout
            className={"colorMode--light page--registrationPage registrationPage--" + page.slug}
            site={site}
            page={page}
            slug={page.slug}
            bannerLabel="Registration"
            hideAd={true}
            layoutVariant={layoutVariant}
        >
            <Flex direction="column" align="center" mb="60px">
                <Wrapper mb={8}>
                    <Heading
                        as="h1"
                        dangerouslySetInnerHTML={{
                            __html: page.title,
                        }}
                        mt={"30px"}
                        id={"title"}
                    />
                    {pageStatus === "default" && (
                        <Presence
                            present={pageStatus === "default"}
                            animationName={{ _open: "fade-in", _closed: "fade-out" }}
                            animationDuration="moderate"
                        >
                            <Stack spacing={4} direction={["column", "row"]}>
                                <Box width={["100%", "50%"]}>
                                    <Heading as="h2">
                                        {page.accountLoginHeading || textDefaults.accountLoginHeading}
                                    </Heading>
                                    <Text>
                                        If you don't have a TCTMD account, you can easily{" "}
                                        <Text
                                            as="span"
                                            textDecoration="underline"
                                            cursor="pointer"
                                            onClick={() => setPageStatus("create-account")}
                                        >
                                            create one
                                        </Text>
                                        . If you need assistance, please contact{" "}
                                        <Link textDecoration="underline" href="mailto:accounthelp@crf.org">
                                            accounthelp@crf.org
                                        </Link>
                                        .
                                    </Text>
                                    {page.registerText && (
                                        <Box mt={4} dangerouslySetInnerHTML={{ __html: page.registerText }} />
                                    )}
                                    <Box mt={6} mb={8} minH="275px">
                                        <CustomLoginForm
                                            redirectUrl={page.redirectUrl}
                                            pardotFormHandler={page.pardotFormHandlerUrl}
                                            slug={page.slug}
                                            preview={page.preview}
                                            oauth={page.oauth}
                                            vendorOktaClientId={vendorOktaClientId}
                                        />
                                    </Box>
                                    <List.Root listStyleType="disc" ml={4} my={3}>
                                        <List.Item>To register, log in with your exclusive TCTMD account.</List.Item>
                                        <List.Item>Register only yourself under your TCTMD account.</List.Item>
                                        <List.Item>A unique TCTMD account is required per registrant.</List.Item>
                                        <List.Item>
                                            Multiple registrations under the same account are not permitted.
                                        </List.Item>
                                    </List.Root>
                                    {page.regBottomText && (
                                        <Box mt={4} dangerouslySetInnerHTML={{ __html: page.regBottomText }} />
                                    )}
                                </Box>
                                <Box width={["100%", "50%"]}>
                                    <Heading as="h2">Don't Have a TCTMD Account?</Heading>
                                    <Text mb={6}>{page.signUpText || textDefaults.signUpText}</Text>
                                    <StyledButton onClick={() => setPageStatus("create-account")}>
                                        CREATE ACCOUNT
                                    </StyledButton>
                                    {page.signUpText2 && (
                                        <Box mt={4} dangerouslySetInnerHTML={{ __html: page.signUpText2 }} />
                                    )}
                                </Box>
                            </Stack>
                        </Presence>
                    )}
                    {pageStatus === "create-account" && (
                        <Presence
                            present={pageStatus === "create-account"}
                            animationName={{ _open: "fade-in", _closed: "fade-out" }}
                            animationDuration="moderate"
                        >
                            <Box mt={4}>
                                <Heading mb={2}>Create Account</Heading>
                                <Button onClick={() => setPageStatus("default")} variant="subtle" colorPalette="gray">
                                    <FaArrowLeft />
                                    Go Back
                                </Button>
                                <CreateAccountSection
                                    redirectUrl={page.redirectUrl}
                                    preview={page.preview}
                                    pardotFormHandler={page.pardotFormHandlerUrl}
                                    slug={page.slug}
                                    setPageStatus={setPageStatus}
                                    locale={locale}
                                    oauth={page.oauth}
                                    vendorOktaClientId={vendorOktaClientId}
                                />
                            </Box>
                        </Presence>
                    )}
                    {pageStatus === "loading" && (
                        <Box mt={4}>
                            <Spinner thickness="3px" speed="0.25s" color="#333" emptyColor="transparent" size="lg" />
                        </Box>
                    )}
                </Wrapper>
            </Flex>
        </Layout>
    )
}

export const getServerSideProps = runMiddleware(async function ({ req, locale, query, user }) {
    const data = await getPageData({
        locale: locale,
        page: {
            slug: query.type,
            type: "registration",
        },
        query: query,
    })

    if (!data.page) {
        return {
            notFound: true,
        }
    }

    data.page.showPageHeader = false

    // pass down the Okta client ID for the vendor when using vendor OAuth flow
    const vendorOktaClientId = data.page.oauth
        ? process.env[
              `OKTA_REG_CLIENT_ID_${locale.toUpperCase().replace(/-/g, "_")}${data.page.preview ? "_PREVIEW" : ""}`
          ] || null
        : null

    return {
        props: {
            section: "registrationPage",
            site: data.site,
            page: data.page,
            locale: locale,
            user: user,
            vendorOktaClientId,
        },
    }
})
